import { useMemo, useRef, useState } from 'react';
import { addDays, format, isMonday, parseISO, previousMonday, startOfDay, subDays } from 'date-fns';
import { Plus } from 'lucide-react';

import {
  useAdminStorefrontPayouts,
  useRecordStorefrontPayout,
  useUpdateStorefrontPayout,
  type StorefrontPayout,
  type StorefrontPayoutStatus,
} from '@/lib/api/storefrontAdminHooks';

import { refusalMessage, shortDate, usd } from './storefrontFormat';
import {
  ActionButton,
  ConfirmDialog,
  Notice,
  Panel,
  Pill,
  Skeleton,
  Stat,
  Td,
  Th,
  inputClass,
} from './ui';

const PAYOUT_TONE: Record<StorefrontPayoutStatus, 'warning' | 'success' | 'neutral'> = {
  pending: 'warning',
  paid: 'success',
  void: 'neutral',
};

/** The last full Monday–Sunday week before today. */
function lastWeek(): { start: string; end: string } {
  const today = startOfDay(new Date());
  const thisMonday = isMonday(today) ? today : previousMonday(today);
  const start = subDays(thisMonday, 7);
  return {
    start: format(start, 'yyyy-MM-dd'),
    end: format(addDays(start, 6), 'yyyy-MM-dd'),
  };
}

const todayIso = () => format(new Date(), 'yyyy-MM-dd');

/** Owners read why a payout was voided: a few words at least. */
const MIN_VOID_REASON = 5;

/** The period no payout covers yet, through today: from the day after the
 *  latest period a pending or paid payout covers, else from when the store
 *  first went live (no earning predates it), else last week's Monday. The
 *  default amount (the balance no payout covers) is then the earnings of
 *  this period, instead of this week's earnings filed under last week. */
function unpaidPeriod(
  history: StorefrontPayout[],
  liveSince: string | null | undefined,
): { start: string; end: string } {
  const end = todayIso();
  const covered = history
    .filter((p) => p.status !== 'void' && p.period_end)
    .map((p) => p.period_end.slice(0, 10))
    .sort()
    .pop();
  const start = covered
    ? format(addDays(parseISO(covered), 1), 'yyyy-MM-dd')
    : liveSince
      ? liveSince.slice(0, 10)
      : lastWeek().start;
  return { start: start <= end ? start : end, end };
}

/** Who recorded, paid or voided a payout (admin emails, newer backends). */
function payoutPeople(p: StorefrontPayout): string[] {
  const out: string[] = [];
  if (p.created_by) out.push(`Recorded by ${p.created_by}`);
  if (p.paid_by) out.push(`Paid by ${p.paid_by}`);
  if (p.voided_by) out.push(`Voided by ${p.voided_by}${p.voided_at ? ` on ${shortDate(p.voided_at)}` : ''}`);
  return out;
}

/** "Pay more than owed": a paid payout above the balance needs a reason,
 *  recorded in the payout's note (the backend notes the overpay too). */
function OverpayField({
  checked,
  onChecked,
  reason,
  onReason,
  owedCents,
}: {
  checked: boolean;
  onChecked: (next: boolean) => void;
  reason: string;
  onReason: (next: string) => void;
  owedCents: number;
}) {
  return (
    <div className="flex flex-col gap-8 border border-warning bg-warning-wash px-10 py-8">
      <label className="flex items-start gap-8 type-body-sm text-ink">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChecked(e.target.checked)}
          className="mt-2"
        />
        <span>
          Pay more than owed. The store is owed {usd(Math.max(owedCents, 0))}; the difference is an
          advance against future earnings.
        </span>
      </label>
      {checked && (
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Reason (required)</span>
          <input
            className={inputClass}
            value={reason}
            onChange={(e) => onReason(e.target.value)}
            maxLength={300}
            placeholder="e.g. advance agreed with the owner on Oct 20"
          />
        </label>
      )}
    </div>
  );
}

/** The backend's per-payout ceiling (`amount_cents` ≤ 10,000,000_00). */
const MAX_PAYOUT_CENTS = 10_000_000_00;

/** Dollars typed by an admin ("1,249.50") → cents, or null. */
function parseDollars(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  return cents > 0 && cents <= MAX_PAYOUT_CENTS ? cents : null;
}

export function PayoutsPanel({
  storefrontId,
  liveSince = null,
}: {
  storefrontId: string;
  /** When the store first went live (its first approval): no earning
   *  predates it, so the first payout's period starts there. */
  liveSince?: string | null;
}) {
  const payouts = useAdminStorefrontPayouts(storefrontId);
  const [formOpen, setFormOpen] = useState(false);
  const [action, setAction] = useState<{
    payout: StorefrontPayout;
    status: 'paid' | 'void';
  } | null>(null);

  const data = payouts.data;
  const pending = useMemo(
    () => (data?.history ?? []).filter((p) => p.status === 'pending'),
    [data?.history],
  );
  const pendingCents = pending.reduce((sum, p) => sum + p.amount_cents, 0);

  return (
    <Panel
      title="Payouts"
      aside={
        <ActionButton
          tone={formOpen ? 'ghost' : 'secondary'}
          className="h-30"
          onClick={() => setFormOpen((v) => !v)}
        >
          {formOpen ? (
            'Close'
          ) : (
            <>
              <Plus size={13} aria-hidden /> Record payout
            </>
          )}
        </ActionButton>
      }
    >
      {payouts.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
          <Skeleton className="h-[8.4rem]" />
          <Skeleton className="h-[8.4rem]" />
          <Skeleton className="h-[8.4rem]" />
        </div>
      ) : payouts.isError ? (
        <Notice tone="danger">Could not load payouts. {refusalMessage(payouts.error)}</Notice>
      ) : data ? (
        <div className="flex flex-col gap-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
            <Stat
              label="Balance owed"
              value={usd(data.balance_cents)}
              hint="Earnings minus paid payouts"
              emphasis
            />
            <Stat
              label="Next payout"
              value={data.next_payout_date ? shortDate(data.next_payout_date) : '—'}
              hint="Weekly, sent by our team"
            />
            <Stat
              label="Pending"
              value={usd(pendingCents)}
              hint={`${pending.length} payout${pending.length === 1 ? '' : 's'} not yet sent`}
            />
          </div>

          {formOpen && (
            <RecordPayoutForm
              storefrontId={storefrontId}
              balanceCents={data.balance_cents}
              pendingCents={pendingCents}
              history={data.history}
              liveSince={liveSince}
              onDone={() => setFormOpen(false)}
            />
          )}

          <div className="border border-line-subtle overflow-x-auto">
            <table className="w-full min-w-[64rem]">
              <thead className="bg-raised border-b border-line-subtle">
                <tr>
                  <Th>Period</Th>
                  <Th align="right">Amount</Th>
                  <Th>Status</Th>
                  <Th>Paid</Th>
                  <Th>Reference</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.history.length === 0 ? (
                  <tr>
                    <Td colSpan={6} className="text-center text-ink-muted py-20">
                      No payouts recorded yet.
                    </Td>
                  </tr>
                ) : (
                  data.history.map((p) => (
                    <tr key={p.id} className="border-b border-line-subtle last:border-b-0">
                      <Td className="whitespace-nowrap">
                        {shortDate(p.period_start)} – {shortDate(p.period_end)}
                        {p.note && (
                          <span className="block type-body-sm text-ink-muted truncate max-w-[26rem]">
                            {p.note}
                          </span>
                        )}
                      </Td>
                      <Td align="right" className={p.status === 'void' ? 'line-through text-ink-muted' : ''}>
                        {usd(p.amount_cents)}
                      </Td>
                      <Td>
                        <span className="flex flex-col gap-4 items-start">
                          <Pill tone={PAYOUT_TONE[p.status] ?? 'neutral'}>{p.status}</Pill>
                          {payoutPeople(p).map((line) => (
                            <span
                              key={line}
                              className="type-body-sm text-ink-muted truncate max-w-[24rem]"
                              title={line}
                            >
                              {line}
                            </span>
                          ))}
                          {p.status === 'void' && p.void_reason && (
                            <span
                              className="type-body-sm text-ink-secondary truncate max-w-[24rem]"
                              title={p.void_reason}
                            >
                              Reason: {p.void_reason}
                            </span>
                          )}
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap text-ink-secondary">
                        {p.paid_at ? shortDate(p.paid_at) : '—'}
                      </Td>
                      <Td className="type-data-sm text-ink-secondary truncate max-w-[18rem]">
                        {p.reference || '—'}
                      </Td>
                      <Td align="right" className="whitespace-nowrap">
                        <div className="flex justify-end gap-6">
                          {p.status === 'pending' && (
                            <ActionButton
                              className="h-28 px-10"
                              onClick={() => setAction({ payout: p, status: 'paid' })}
                            >
                              Mark paid
                            </ActionButton>
                          )}
                          {p.status !== 'void' && (
                            <ActionButton
                              tone="ghost"
                              className="h-28 px-10"
                              onClick={() => setAction({ payout: p, status: 'void' })}
                            >
                              Void
                            </ActionButton>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="type-body-sm text-ink-muted leading-130">
            Payouts are sent by hand outside this app. Record each one here so the owner&apos;s
            balance stays right. Never enter bank or tax details.
          </p>
        </div>
      ) : null}

      {action && (
        <PayoutStatusDialog
          storefrontId={storefrontId}
          payout={action.payout}
          status={action.status}
          balanceCents={data?.balance_cents ?? 0}
          onClose={() => setAction(null)}
        />
      )}
    </Panel>
  );
}

function PayoutStatusDialog({
  storefrontId,
  payout,
  status,
  balanceCents,
  onClose,
}: {
  storefrontId: string;
  payout: StorefrontPayout;
  status: 'paid' | 'void';
  /** Earnings minus paid payouts (this pending one is not in it yet). */
  balanceCents: number;
  onClose: () => void;
}) {
  const update = useUpdateStorefrontPayout(storefrontId);
  const [reference, setReference] = useState(payout.reference ?? '');
  const [overpay, setOverpay] = useState(false);
  const [reason, setReason] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const period = `${shortDate(payout.period_start)} – ${shortDate(payout.period_end)}`;
  const paying = status === 'paid';
  const voidReasonOk = voidReason.trim().length >= MIN_VOID_REASON;
  const periodOpen = paying && payout.period_end > todayIso();
  const overBalance = paying && payout.amount_cents > balanceCents;

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={status === 'paid' ? 'Mark payout paid' : 'Void payout'}
      tone={status === 'void' ? 'danger' : 'primary'}
      description={
        status === 'paid'
          ? `${usd(payout.amount_cents)} for ${period}. This lowers the owner's balance.`
          : payout.status === 'paid'
            ? `${usd(payout.amount_cents)} for ${period} was recorded as paid. Voiding it adds the amount back to the owner's balance.`
            : `${usd(payout.amount_cents)} for ${period} will not be sent.`
      }
      confirmLabel={status === 'paid' ? 'Mark paid' : 'Void payout'}
      pending={update.isPending}
      confirmDisabled={
        periodOpen || (overBalance && (!overpay || !reason.trim())) || (!paying && !voidReasonOk)
      }
      error={error}
      onConfirm={async () => {
        setError(null);
        try {
          await update.mutateAsync({
            payoutId: payout.id,
            status,
            ...(paying
              ? {
                  paid_at: new Date().toISOString(),
                  reference: reference.trim() || null,
                  ...(overBalance && overpay
                    ? { allow_overpay: true, note: `Paid more than owed: ${reason.trim()}` }
                    : {}),
                }
              : { void_reason: voidReason.trim() }),
          });
          onClose();
        } catch (err) {
          setError(refusalMessage(err));
        }
      }}
    >
      {periodOpen && (
        <Notice tone="warning">
          This payout covers a period that ends on {shortDate(payout.period_end)}. Mark it paid once
          the period has ended.
        </Notice>
      )}
      {overBalance && !periodOpen && (
        <OverpayField
          checked={overpay}
          onChecked={setOverpay}
          reason={reason}
          onReason={setReason}
          owedCents={balanceCents}
        />
      )}
      {status === 'void' && (
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Reason (required)</span>
          <input
            className={inputClass}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. “Recorded twice; the October payout covers it.”"
            aria-describedby="void-reason-help"
            autoFocus
          />
          <span id="void-reason-help" className="type-body-sm text-ink-muted leading-130">
            The owner sees it in their payout history and a &ldquo;Payout cancelled&rdquo; notice.
            Plain text, at least {MIN_VOID_REASON} characters.
          </span>
        </label>
      )}
      {status === 'paid' && (
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Transfer reference (optional)</span>
          <input
            className={inputClass}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
            placeholder="e.g. the transfer confirmation number"
          />
        </label>
      )}
    </ConfirmDialog>
  );
}

function RecordPayoutForm({
  storefrontId,
  balanceCents,
  pendingCents,
  history,
  liveSince,
  onDone,
}: {
  storefrontId: string;
  balanceCents: number;
  /** Pending payouts already cover this much of the balance (the balance
   *  only drops when a payout is paid). */
  pendingCents: number;
  history: StorefrontPayout[];
  liveSince: string | null;
  onDone: () => void;
}) {
  const record = useRecordStorefrontPayout(storefrontId);
  // Taken once when the form opens, so a refetch never moves the dates.
  const [week] = useState(() => unpaidPeriod(history, liveSince));
  // What no payout covers yet: the default amount, and the ceiling the
  // warning below checks (a pending payout plus a new one for the full
  // balance would pay the owner twice).
  const uncoveredCents = balanceCents - pendingCents;
  const [start, setStart] = useState(week.start);
  const [end, setEnd] = useState(week.end);
  const [amount, setAmount] = useState(
    uncoveredCents > 0 ? (uncoveredCents / 100).toFixed(2) : '',
  );
  const [status, setStatus] = useState<'paid' | 'pending'>('paid');
  const [paidAt, setPaidAt] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [overpay, setOverpay] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cents = parseDollars(amount);
  const periodOk = Boolean(start && end && start <= end);
  // The backend refuses a paid payout for a period that has not ended.
  const periodOpen = status === 'paid' && Boolean(end) && end > todayIso();
  // Paid: above the balance needs "pay more than owed". Pending: above
  // what no other payout covers could never be marked paid as is.
  const overCents = status === 'paid' ? balanceCents : uncoveredCents;
  const overBalance = cents != null && cents > overCents;
  const overpayOk = !overBalance || (overpay && Boolean(reason.trim()));
  const valid =
    cents != null &&
    periodOk &&
    !periodOpen &&
    overpayOk &&
    (status !== 'paid' || Boolean(paidAt));
  // Set synchronously so a double click records one payout, not two. The
  // key stays the same for this form's retries.
  const inFlight = useRef(false);
  const idempotencyKey = useRef(crypto.randomUUID());

  return (
    <form
      className="flex flex-col gap-14 p-16 border border-line bg-raised"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!valid || cents == null || inFlight.current) return;
        inFlight.current = true;
        setError(null);
        try {
          await record.mutateAsync({
            idempotencyKey: idempotencyKey.current,
            period_start: start,
            period_end: end,
            amount_cents: cents,
            status,
            paid_at: status === 'paid' ? new Date(paidAt).toISOString() : null,
            reference: reference.trim() || null,
            note:
              [note.trim(), overBalance && overpay ? `Paid more than owed: ${reason.trim()}` : '']
                .filter(Boolean)
                .join(' · ') || null,
            ...(overBalance && overpay ? { allow_overpay: true } : {}),
          });
          onDone();
        } catch (err) {
          setError(refusalMessage(err));
        } finally {
          inFlight.current = false;
        }
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Period start</span>
          <input type="date" className={inputClass} value={start} onChange={(e) => setStart(e.target.value)} required />
        </label>
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Period end</span>
          <input type="date" className={inputClass} value={end} onChange={(e) => setEnd(e.target.value)} required />
        </label>
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Amount (USD)</span>
          <input
            className={inputClass}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            required
          />
        </label>
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Status</span>
          <select
            className={inputClass}
            value={status}
            onChange={(e) => setStatus(e.target.value as 'paid' | 'pending')}
          >
            <option value="paid">Paid (sent)</option>
            <option value="pending">Pending (to send)</option>
          </select>
        </label>
        {status === 'paid' && (
          <label className="flex flex-col gap-6">
            <span className="type-label-xs text-ink-muted">Paid at</span>
            <input
              type="datetime-local"
              className={inputClass}
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              required
            />
          </label>
        )}
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Reference</span>
          <input
            className={inputClass}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
            placeholder="Transfer confirmation"
          />
        </label>
        <label className="flex flex-col gap-6 sm:col-span-2">
          <span className="type-label-xs text-ink-muted">Note</span>
          <input
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Internal note (optional)"
          />
        </label>
      </div>
      {!periodOk && start && end && (
        <span className="type-body-sm text-warning">The period ends before it starts.</span>
      )}
      {amount && cents == null && (
        <span className="type-body-sm text-warning">
          Enter an amount above $0 and at most $10,000,000, like 1249.50.
        </span>
      )}
      {periodOpen && (
        <span className="type-body-sm text-warning">
          A paid payout must cover a period that has ended (today or earlier). Record it as
          pending until then.
        </span>
      )}
      {overBalance && (
        <>
          <Notice tone="warning">
            {pendingCents > 0 && status === 'pending'
              ? `${usd(cents)} is more than the balance no payout covers yet (${usd(Math.max(uncoveredCents, 0))}: ${usd(balanceCents)} owed, ${usd(pendingCents)} already pending). Mark the pending payout paid instead if this is the same transfer.`
              : `${usd(cents)} is more than the balance owed (${usd(Math.max(overCents, 0))}).`}
          </Notice>
          <OverpayField
            checked={overpay}
            onChecked={setOverpay}
            reason={reason}
            onReason={setReason}
            owedCents={overCents}
          />
        </>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="flex items-center justify-between gap-12 flex-wrap">
        <span className="type-body-sm text-ink-muted">
          Defaults to every day no payout covers yet, through today, and the balance no payout
          covers. Change both together: the screen cannot split earnings by date.
        </span>
        <ActionButton type="submit" tone="primary" disabled={!valid || record.isPending}>
          {record.isPending ? 'Saving…' : 'Record payout'}
        </ActionButton>
      </div>
    </form>
  );
}
